import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, ShoppingBag, Crown } from "lucide-react";
import { ShopLayout } from "../../components/shop/ShopLayout";
import { ProductCard } from "../../components/shop/ProductCard";
import { shopApi, fmt, apiImageUrl } from "../../lib/shopApi";
import { useCart } from "../../context/CartContext";

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [sizeLabel, setSizeLabel] = useState(null);
  const [options, setOptions] = useState({});
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setActiveImage(0);
    setQty(1);
    shopApi
      .get(`/shop/products/${slug}`)
      .then((r) => {
        setProduct(r.data.product);
        setRelated(r.data.related);
        setSizeLabel(r.data.product.sizes?.[0]?.label || null);
        const defaults = {};
        for (const [key, list] of [["length", r.data.product.options?.lengths], ["color", r.data.product.options?.colors], ["thickness", r.data.product.options?.thicknesses]]) {
          if (list?.length) defaults[key] = list[0];
        }
        setOptions(defaults);
        if (r.data.product.seo_title) document.title = r.data.product.seo_title;
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const price = useMemo(() => {
    if (!product) return 0;
    if (product.sizes?.length) {
      const s = product.sizes.find((x) => x.label === sizeLabel) || product.sizes[0];
      return s.price_cents;
    }
    return product.sale_price_cents || product.price_cents;
  }, [product, sizeLabel]);

  const soldOut = product && (product.status === "sold_out" || product.total_stock <= 0);

  const handleAdd = () => {
    addItem(product, sizeLabel, qty, options);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  if (notFound) {
    return (
      <ShopLayout title="Product Not Found | Loctician Carlocs">
        <div className="max-w-3xl mx-auto px-5 py-32 text-center" data-testid="product-not-found">
          <p className="text-[#F5EFE3]/50 mb-6">This product is no longer available.</p>
          <Link to="/shop" className="text-[#C9A24B] underline underline-offset-4">Back to the shop</Link>
        </div>
      </ShopLayout>
    );
  }

  if (!product) {
    return (
      <ShopLayout title="Loading | Loctician Carlocs">
        <div className="py-40 text-center text-[#F5EFE3]/40 text-sm">Loading product…</div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout title={product.seo_title || `${product.name} | Loctician Carlocs`}>
      <div className="max-w-7xl mx-auto px-5 py-8 md:py-14" data-testid="product-detail">
        <Link to="/shop" className="inline-flex items-center gap-2 text-[#F5EFE3]/50 hover:text-[#C9A24B] text-xs uppercase tracking-widest mb-8 transition-colors" data-testid="back-to-shop">
          <ArrowLeft className="w-4 h-4" /> Back to Shop
        </Link>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div>
            <motion.div
              key={activeImage}
              initial={{ opacity: 0.4 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="aspect-square rounded-2xl overflow-hidden border border-[#C9A24B]/15 bg-[#181818] mb-4"
            >
              <img
                src={apiImageUrl(product.images?.[activeImage])}
                alt={`${product.name} - Johannesburg locs product`}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </motion.div>
            {product.images?.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    data-testid={`product-thumb-${i}`}
                    className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors duration-300 ${activeImage === i ? "border-[#C9A24B]" : "border-transparent opacity-60 hover:opacity-100"}`}
                  >
                    <img src={apiImageUrl(img)} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <p className="text-[#C9A24B] text-xs uppercase tracking-[0.3em] mb-3 flex items-center gap-2">
              <Crown className="w-4 h-4" /> {product.category}
            </p>
            <h1 className="font-serif text-3xl md:text-5xl leading-tight mb-4">{product.name}</h1>

            {(product.made_to_order || product.category === "Locs Extensions") && (
              <p data-testid="made-to-order-badge" className="inline-flex items-center gap-2 border border-[#C9A24B]/40 text-[#C9A24B] text-[11px] uppercase tracking-widest px-4 py-2 rounded-full mb-5">
                <Crown className="w-3.5 h-3.5" /> Made to order — ready in {product.lead_time_text || "7-15 days"}
              </p>
            )}

            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-[#C9A24B] text-2xl font-semibold" data-testid="product-price">{fmt(price)}</span>
              {product.sale_price_cents && !product.sizes?.length && (
                <span className="text-[#F5EFE3]/40 line-through">{fmt(product.price_cents)}</span>
              )}
            </div>

            <p className="text-[#F5EFE3]/65 font-light leading-relaxed mb-8">{product.description}</p>

            {/* Sizes */}
            {product.sizes?.length > 0 && (
              <div className="mb-6">
                <p className="text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-3">Size</p>
                <div className="flex flex-wrap gap-3">
                  {product.sizes.map((s) => (
                    <button
                      key={s.label}
                      onClick={() => setSizeLabel(s.label)}
                      data-testid={`size-${s.label}`}
                      className={`px-5 py-3 rounded-full text-sm border transition-all duration-300 ${
                        sizeLabel === s.label
                          ? "bg-[#C9A24B] text-[#1C1C1C] border-[#C9A24B] font-semibold"
                          : "border-[#C9A24B]/25 text-[#F5EFE3]/70 hover:border-[#C9A24B]/60"
                      }`}
                    >
                      {s.label} · {fmt(s.price_cents)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Extension options: length / color / thickness */}
            {[["length", "Length", product.options?.lengths], ["color", "Color", product.options?.colors], ["thickness", "Thickness", product.options?.thicknesses]].map(([key, label, list]) =>
              list?.length ? (
                <div className="mb-6" key={key}>
                  <p className="text-xs uppercase tracking-widest text-[#F5EFE3]/50 mb-3">{label}</p>
                  <div className="flex flex-wrap gap-3">
                    {list.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setOptions((o) => ({ ...o, [key]: opt }))}
                        data-testid={`option-${key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`px-5 py-3 rounded-full text-sm border transition-all duration-300 ${
                          options[key] === opt
                            ? "bg-[#C9A24B] text-[#1C1C1C] border-[#C9A24B] font-semibold"
                            : "border-[#C9A24B]/25 text-[#F5EFE3]/70 hover:border-[#C9A24B]/60"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null
            )}

            {/* Qty + Add */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex items-center border border-[#C9A24B]/25 rounded-full">
                <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="p-3.5 text-[#F5EFE3]/60 hover:text-[#C9A24B] transition-colors">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-10 text-center font-semibold" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" className="p-3.5 text-[#F5EFE3]/60 hover:text-[#C9A24B] transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={handleAdd}
                disabled={soldOut}
                data-testid="add-to-cart-btn"
                className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-full text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                  soldOut
                    ? "bg-[#3a3a3a] text-[#F5EFE3]/40 cursor-not-allowed"
                    : added
                      ? "bg-[#F5EFE3] text-[#1C1C1C]"
                      : "bg-[#C9A24B] text-[#1C1C1C] hover:shadow-[0_10px_30px_rgba(201,162,75,0.3)] hover:-translate-y-0.5"
                }`}
              >
                <ShoppingBag className="w-4 h-4" />
                {soldOut ? "Sold Out" : added ? "Added to Cart ✓" : "Add to Cart"}
              </button>
            </div>

            {product.total_stock > 0 && product.total_stock <= 5 && (
              <p className="text-[#C9A24B]/80 text-xs uppercase tracking-widest mb-6" data-testid="low-stock-note">
                Only {product.total_stock} left in stock
              </p>
            )}
          </div>
        </div>

        {/* You may also like */}
        {related.length > 0 && (
          <div className="mt-20" data-testid="related-products">
            <h2 className="font-serif text-2xl md:text-3xl mb-8">You may also <span className="italic text-[#C9A24B]">like</span></h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </ShopLayout>
  );
};

export default ProductDetailPage;
